import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, NavLink, Outlet, useOutletContext } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ReviewPager, StarsRow } from '../components/ReviewTimeline'
import {
  api,
  formatVnd,
  type AdminCard,
  type AdminDeposit,
  type AdminMember,
  type AdminMemberDetail,
  type AdminPendingOrder,
  type AdminQueue,
  type AdminReview,
  type AdminSummary,
  type AdminTransaction,
  type AdminUser,
  type Paged,
} from '../lib/api'
import { StatusBadge, formatDateTime, type Tone } from './AccountPage'
import { SearchBox, useQueryState } from './ContentPages'

type AdminCtx = { summary: AdminSummary | null; refresh: () => void }

const MENU: { to: string; label: string; icon: string; badge?: keyof AdminSummary }[] = [
  { to: '/admin/reviews', label: 'Duyệt review', icon: 'fa-star', badge: 'reviews' },
  { to: '/admin/dat-ho', label: 'Duyệt đặt hộ', icon: 'fa-cart-shopping', badge: 'queue' },
  { to: '/admin/don-cho', label: 'Đơn chờ giao', icon: 'fa-clock', badge: 'pending_orders' },
  { to: '/admin/chuyen-khoan', label: 'Chuyển khoản', icon: 'fa-building-columns', badge: 'deposits' },
  { to: '/admin/the-cao', label: 'Thẻ cào', icon: 'fa-credit-card', badge: 'cards' },
  { to: '/admin/tra-cuu', label: 'Tra cứu đơn', icon: 'fa-magnifying-glass' },
  { to: '/admin/tra-cuu-dat-ho', label: 'Tra cứu đặt hộ', icon: 'fa-list-check' },
  { to: '/admin/thanh-vien', label: 'Số dư thành viên', icon: 'fa-users' },
  { to: '/admin/key-intro', label: 'Bài giới thiệu key', icon: 'fa-pen-to-square' },
]

const REVIEW_STATUS: Record<string, [string, Tone]> = {
  cho_xu_li: ['Chờ duyệt', 'warn'],
  chap_nhan: ['Đã duyệt', 'ok'],
  huy_bo: ['Từ chối', 'danger'],
}

const QUEUE_STATUS: Record<string, [string, Tone]> = {
  cho_xu_li: ['Chờ xử lí', 'warn'],
  thanh_cong: ['Thành công', 'ok'],
  that_bai: ['Thất bại', 'danger'],
}

const DEPOSIT_STATUS: Record<string, [string, Tone]> = {
  chua_thanh_toan: ['Chờ duyệt', 'warn'],
  da_thanh_toan: ['Đã duyệt', 'ok'],
  huy_bo: ['Đã hủy', 'danger'],
}

const CARD_STATUS: Record<string, [string, Tone]> = {
  choduyet: ['Chờ duyệt', 'warn'],
  thanhcong: ['Thành công', 'ok'],
  thatbai: ['Thất bại', 'danger'],
}

const PAY_LABEL: Record<string, string> = { xu: 'Ví xu', gem: 'Ví gem', bank: 'Chuyển khoản' }

const QUEUE_DONE_PICKS = [
  'Cảm ơn bạn đã đặt hàng tại shop. Đơn hàng đã được giao.',
  'Cảm ơn bạn đã ủng hộ shop nhé! Vui lòng đánh giá nếu bạn hài lòng!',
  'Đơn hàng đã được giao, nếu thấy shop hữu ích hãy chia sẽ nhiều hơn!',
]

const num = (n: number) => n.toLocaleString('vi-VN')

function money(n: number, pay: string | null) {
  if (pay === 'xu') return `${num(n)} xu`
  if (pay === 'gem') return `${num(n)} gem`
  return formatVnd(n)
}

/** Ví nhận tiền hoàn: trả bằng gem về gem, còn lại về xu. */
const refundUnit = (pay: string | null) => (pay === 'gem' ? 'gem' : 'xu')

function useAdmin() {
  return useOutletContext<AdminCtx>()
}

export function AdminLayout() {
  const { user, loading } = useAuth()
  const [summary, setSummary] = useState<AdminSummary | null>(null)
  const refresh = useCallback(() => {
    api.admin
      .summary()
      .then((r) => setSummary(r.summary))
      .catch(() => null)
  }, [])

  useEffect(() => {
    if (user?.role === 'admin') refresh()
  }, [user, refresh])

  if (loading) return <p className="muted">Đang tải…</p>
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'admin') return <p className="note">Không có quyền.</p>

  return (
    <div className="acc adm">
      <div className="acc-workspace adm-workspace">
        <nav className="acc-nav" aria-label="Menu quản trị">
          <h2>
            <Link to="/admin">Quản trị</Link>
          </h2>
          <ul>
            {MENU.map((m) => {
              const count = m.badge && summary ? summary[m.badge] : 0
              return (
                <li key={m.to}>
                  <NavLink to={m.to} className={({ isActive }) => `acc-nav-item${isActive ? ' active' : ''}`}>
                    <i className={`fa-solid ${m.icon}`} aria-hidden /> {m.label}
                    {count ? <span className="adm-count">{num(count)}</span> : null}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </nav>
        <section className="acc-main adm-main">
          <Outlet context={{ summary, refresh } satisfies AdminCtx} />
        </section>
      </div>
    </div>
  )
}

export function AdminHomePage() {
  const { summary } = useAdmin()
  return (
    <>
      <AdminHead title="Việc cần xử lí" />
      <div className="adm-tiles">
        {MENU.filter((m) => m.badge).map((m) => {
          const n = summary && m.badge ? summary[m.badge] : null
          return (
            <Link key={m.to} to={m.to} className={`adm-tile${n ? ' hot' : ''}`}>
              <i className={`fa-solid ${m.icon}`} aria-hidden />
              <strong>{n == null ? '…' : num(n)}</strong>
              <span>{m.label}</span>
            </Link>
          )
        })}
      </div>
    </>
  )
}

/* ---------- Khung dùng chung ---------- */

function AdminHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="acc-head adm-head">
      <h2>{title}</h2>
      {children}
    </div>
  )
}

function useList<T>(load: () => Promise<Paged<T>>, key: string) {
  const [data, setData] = useState<Paged<T> | null>(null)
  const [err, setErr] = useState('')
  const [tick, setTick] = useState(0)
  useEffect(() => {
    let alive = true
    setErr('')
    load()
      .then((r) => alive && setData(r))
      .catch((e: Error) => {
        if (!alive) return
        setErr(e.message)
        setData(null)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick])
  return { data, err, reload: () => setTick((t) => t + 1) }
}

function useAfter(reload: () => void) {
  const { refresh } = useAdmin()
  const [flash, setFlash] = useState('')
  const [modal, setModal] = useState<ReactNode>(null)
  const close = useCallback(() => setModal(null), [])
  const after = (msg: string) => {
    setModal(null)
    setFlash(msg)
    reload()
    refresh()
  }
  return { flash, after, modal, setModal, close }
}

function Feedback({ flash, err }: { flash?: string; err?: string }) {
  return (
    <>
      {flash ? (
        <p className="flash-ok" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden /> {flash}
        </p>
      ) : null}
      {err ? <p className="note">{err}</p> : null}
    </>
  )
}

function StatusTabs({
  value,
  options,
  onChange,
}: {
  value: string
  options: [string, string][]
  onChange: (v: string) => void
}) {
  return (
    <div className="adm-tabs" role="tablist">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={value === v}
          className={`adm-tab${value === v ? ' active' : ''}`}
          onClick={() => onChange(v)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function ListFoot<T>({ data, empty, onPage }: { data: Paged<T> | null; empty: string; onPage: (p: number) => void }) {
  if (!data) return <p className="muted">Đang tải…</p>
  if (!data.items.length) return <p className="muted">{empty}</p>
  return (
    <div className="adm-foot">
      <span className="muted">{num(data.total)} mục</span>
      <ReviewPager page={data.page} totalPages={data.total_pages} onPage={onPage} />
    </div>
  )
}

function UserCell({ u }: { u: AdminUser }) {
  return (
    <>
      <Link to={`/admin/thanh-vien?q=${u.id}`}>{u.name || `#${u.id}`}</Link>
      {u.email ? (
        <small>
          {u.email.split('@')[0]}
          {u.email.includes('@') ? (
            <>
              <wbr />@{u.email.slice(u.email.indexOf('@') + 1)}
            </>
          ) : null}
        </small>
      ) : null}
    </>
  )
}

function Modal({
  title,
  onClose,
  wide,
  children,
}: {
  title: string
  onClose: () => void
  wide?: boolean
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="amc-modal-backdrop" onClick={onClose}>
      <div
        className={`amc-modal adm-modal${wide ? ' adm-modal-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  )
}

function useSubmit(run: () => Promise<unknown>, onDone: () => void) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  async function submit(e?: FormEvent) {
    e?.preventDefault()
    setBusy(true)
    setErr('')
    try {
      await run()
      onDone()
    } catch (ex) {
      setErr((ex as Error).message)
      setBusy(false)
    }
  }
  return { busy, err, submit }
}

function ModalActions({ busy, confirm, tone, disabled, onClose }: {
  busy: boolean
  confirm: string
  tone?: 'primary' | 'ok' | 'danger'
  disabled?: boolean
  onClose: () => void
}) {
  return (
    <div className="amc-modal-actions">
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        Hủy
      </button>
      <button type="submit" className={`btn btn-${tone || 'primary'}`} disabled={busy || disabled}>
        {busy ? 'Đang xử lí…' : confirm}
      </button>
    </div>
  )
}

function NoteModal({
  title,
  lead,
  label,
  placeholder,
  required,
  rows = 4,
  picks = [],
  confirm,
  tone,
  onClose,
  run,
  onDone,
}: {
  title: string
  lead?: ReactNode
  label?: string
  placeholder?: string
  required?: boolean
  rows?: number
  picks?: string[]
  confirm: string
  tone?: 'primary' | 'ok' | 'danger'
  onClose: () => void
  run: (note: string) => Promise<unknown>
  onDone: () => void
}) {
  const [note, setNote] = useState('')
  const { busy, err, submit } = useSubmit(() => run(note.trim()), onDone)
  return (
    <Modal title={title} onClose={onClose}>
      {lead}
      <form className="form-stack" onSubmit={submit}>
        {label ? (
          <label>
            {label}
            <textarea
              rows={rows}
              value={note}
              placeholder={placeholder}
              onChange={(e) => setNote(e.target.value)}
              autoFocus
            />
          </label>
        ) : null}
        {picks.length ? (
          <div className="adm-picks">
            {picks.map((p) => (
              <button key={p} type="button" className="adm-pick" onClick={() => setNote(p)}>
                {p}
              </button>
            ))}
          </div>
        ) : null}
        {err ? <p className="note">{err}</p> : null}
        <ModalActions busy={busy} confirm={confirm} tone={tone} disabled={required && !note.trim()} onClose={onClose} />
      </form>
    </Modal>
  )
}

/* ---------- Duyệt review ---------- */

const REVIEW_TABS: [string, string][] = [
  ['cho_xu_li', 'Chờ duyệt'],
  ['chap_nhan', 'Đã duyệt'],
  ['huy_bo', 'Từ chối'],
]

export function AdminReviewsPage() {
  const [params, set] = useQueryState()
  const status = params.get('status') || 'cho_xu_li'
  const page = Number(params.get('page')) || 1
  const { data, err, reload } = useList(() => api.admin.reviews({ status, page }), params.toString())
  const { flash, after, modal, setModal, close } = useAfter(reload)
  const [picks, setPicks] = useState<string[]>([])

  useEffect(() => {
    api
      .replyTemplates()
      .then((r) => setPicks(r.templates))
      .catch(() => null)
  }, [])

  function decide(r: AdminReview, approve: boolean) {
    setModal(
      <NoteModal
        title={approve ? 'Duyệt đánh giá' : 'Từ chối đánh giá'}
        lead={
          <blockquote className="adm-quote">
            <StarsRow n={r.stars} />
            <p>{r.content}</p>
          </blockquote>
        }
        label={approve ? 'Phản hồi của shop' : 'Lí do'}
        required
        picks={approve ? picks : []}
        confirm={approve ? 'Duyệt' : 'Từ chối'}
        tone={approve ? 'ok' : 'danger'}
        onClose={close}
        run={(note) => api.admin.decideReview(r.id, approve, note)}
        onDone={() => after(approve ? 'Đã duyệt đánh giá.' : 'Đã từ chối đánh giá.')}
      />,
    )
  }

  return (
    <>
      <AdminHead title="Duyệt review" />
      <StatusTabs value={status} options={REVIEW_TABS} onChange={(v) => set({ status: v === 'cho_xu_li' ? null : v, page: null })} />
      <Feedback flash={flash} err={err} />
      {data?.items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table adm-table">
            <thead>
              <tr>
                <th>Khách</th>
                <th>Sản phẩm</th>
                <th>Đánh giá</th>
                <th>Ngày</th>
                <th>{status === 'cho_xu_li' ? '' : 'Phản hồi'}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id}>
                  <td>
                    <UserCell u={r.user} />
                  </td>
                  <td>{r.product_name || '—'}</td>
                  <td className="adm-wide">
                    <StarsRow n={r.stars} />
                    <p className="adm-text">{r.content}</p>
                  </td>
                  <td className="acc-nowrap">{formatDateTime(r.created_at)}</td>
                  <td className="adm-status">
                    {r.status === 'cho_xu_li' ? (
                      <div className="adm-actions">
                        <button type="button" className="btn btn-sm btn-ok" onClick={() => decide(r, true)}>
                          <i className="fa-solid fa-check" aria-hidden /> Duyệt
                        </button>
                        <button type="button" className="btn btn-sm btn-line" onClick={() => decide(r, false)}>
                          <i className="fa-solid fa-xmark" aria-hidden /> Từ chối
                        </button>
                      </div>
                    ) : (
                      <>
                        <StatusBadge status={r.status} map={REVIEW_STATUS} />
                        {r.admin_note ? <small className="acc-admin-note">{r.admin_note}</small> : null}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ListFoot data={data} empty="Không có đánh giá." onPage={(p) => set({ page: p })} />
      {modal}
    </>
  )
}

/* ---------- Đơn đặt hộ: duyệt + tra cứu ---------- */

const QUEUE_TABS: [string, string][] = [
  ['all', 'Tất cả'],
  ['cho_xu_li', 'Chờ xử lí'],
  ['thanh_cong', 'Thành công'],
  ['that_bai', 'Thất bại'],
]

function QueueMedia({ q }: { q: AdminQueue }) {
  const [bad, setBad] = useState(false)
  if (!q.image || bad) return <span className="adm-thumb adm-thumb-empty"><i className="fa-solid fa-gift" aria-hidden /></span>
  if (q.media === 'video') {
    return <video className="adm-thumb" src={q.image} muted loop autoPlay playsInline onError={() => setBad(true)} />
  }
  return <img className="adm-thumb" src={q.image} alt="" loading="lazy" onError={() => setBad(true)} />
}

export function AdminQueuePage({ lookup = false }: { lookup?: boolean }) {
  const [params, set] = useQueryState()
  const status = lookup ? params.get('status') || 'all' : 'cho_xu_li'
  const q = params.get('q') || ''
  const page = Number(params.get('page')) || 1
  const { data, err, reload } = useList(() => api.admin.queue({ status, q, page }), `${lookup}:${params}`)
  const { flash, after, modal, setModal, close } = useAfter(reload)

  function complete(row: AdminQueue) {
    setModal(
      <NoteModal
        title="Hoàn thành đơn đặt hộ"
        lead={<p className="adm-lead">{row.product_name} · {row.character_name || '—'}</p>}
        label="Ghi chú cho khách"
        required
        picks={QUEUE_DONE_PICKS}
        confirm="Thành công"
        tone="ok"
        onClose={close}
        run={(note) => api.admin.completeQueue(row.id, note)}
        onDone={() => after(`Đơn #${row.id} đã giao.`)}
      />,
    )
  }

  function refund(row: AdminQueue) {
    setModal(
      <NoteModal
        title="Hủy đơn và hoàn tiền"
        lead={
          <p className="adm-lead">
            Hoàn <strong>{money(row.total, row.pay)}</strong> cho {row.user.name}
          </p>
        }
        label="Lí do"
        required
        confirm="Hoàn tiền"
        tone="danger"
        onClose={close}
        run={(note) => api.admin.refundQueue(row.id, note)}
        onDone={() => after(`Đã hoàn tiền đơn #${row.id}.`)}
      />,
    )
  }

  return (
    <>
      <AdminHead title={lookup ? 'Tra cứu đặt hộ' : 'Duyệt đặt hộ'}>
        <SearchBox value={q} onSearch={(v) => set({ q: v || null, page: null })} placeholder="Mã đơn, nhân vật, tên, email" />
      </AdminHead>
      {lookup ? (
        <StatusTabs value={status} options={QUEUE_TABS} onChange={(v) => set({ status: v === 'all' ? null : v, page: null })} />
      ) : null}
      <Feedback flash={flash} err={err} />
      {data?.items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table adm-table">
            <thead>
              <tr>
                <th>Đơn</th>
                <th>Khách</th>
                <th>Sản phẩm</th>
                <th>Nhân vật</th>
                <th>Ghi chú</th>
                <th>Tổng</th>
                <th>Ngày</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.id}>
                  <td>
                    #{row.id}
                    {row.trans_id ? (
                      <small>
                        <Link className="adm-mono" to={`/admin/tra-cuu?code=${row.trans_id}`}>
                          {row.trans_id.slice(0, 10)}
                        </Link>
                      </small>
                    ) : null}
                  </td>
                  <td>
                    <UserCell u={row.user} />
                  </td>
                  <td>
                    <div className="adm-prod">
                      <QueueMedia q={row} />
                      <div>
                        <strong>{row.product_name}</strong>
                        <small>
                          {[row.group, row.source.name].filter(Boolean).join(' · ')}
                          {row.source.link ? (
                            <>
                              {' '}
                              <a href={row.source.link} target="_blank" rel="noreferrer" aria-label="Mở shop">
                                <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden />
                              </a>
                            </>
                          ) : null}
                        </small>
                        {row.cape_skin ? (
                          <small>
                            <a href={row.cape_skin} target="_blank" rel="noreferrer">
                              Skin cape
                            </a>
                          </small>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="adm-mono acc-nowrap">{row.character_name || '—'}</td>
                  <td className="adm-text">{row.note || '—'}</td>
                  <td className="acc-nowrap">
                    {money(row.total, row.pay)}
                    <small>{PAY_LABEL[row.pay]}</small>
                  </td>
                  <td className="acc-nowrap">{formatDateTime(row.created_at)}</td>
                  <td className="adm-status">
                    {row.status === 'cho_xu_li' ? (
                      <div className="adm-actions">
                        <button type="button" className="btn btn-sm btn-ok" onClick={() => complete(row)}>
                          <i className="fa-solid fa-check" aria-hidden /> Thành công
                        </button>
                        {row.pay !== 'bank' ? (
                          <button type="button" className="btn btn-sm btn-line" onClick={() => refund(row)}>
                            <i className="fa-solid fa-rotate-left" aria-hidden /> Hoàn tiền
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <StatusBadge status={row.status} map={QUEUE_STATUS} />
                        {row.admin_note ? <small className="acc-admin-note">{row.admin_note}</small> : null}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ListFoot data={data} empty={lookup ? 'Không tìm thấy đơn.' : 'Không có đơn chờ xử lí.'} onPage={(p) => set({ page: p })} />
      {modal}
    </>
  )
}

/* ---------- Đơn chờ giao thủ công ---------- */

export function AdminPendingOrdersPage() {
  const [params, set] = useQueryState()
  const page = Number(params.get('page')) || 1
  const { data, err, reload } = useList(() => api.admin.pendingOrders({ page }), params.toString())
  const { flash, after, modal, setModal, close } = useAfter(reload)

  function open(o: AdminPendingOrder, kind: 'deliver' | 'notify' | 'refund' | 'cancel') {
    const common = { onClose: close }
    if (kind === 'deliver') {
      setModal(
        <NoteModal
          {...common}
          title={`Giao hàng cho ${o.user.name || 'khách'}`}
          lead={<p className="adm-lead">{o.product_name} · SL {o.quantity}</p>}
          label={`Sản phẩm (${o.quantity} dòng)`}
          placeholder="Mỗi dòng là 1 sản phẩm"
          rows={6}
          required
          confirm="Giao hàng"
          tone="ok"
          run={(items) => api.admin.deliverOrder(o.id, items)}
          onDone={() => after(`Đã giao đơn ${o.trans_id}.`)}
        />,
      )
    } else if (kind === 'notify') {
      setModal(
        <NoteModal
          {...common}
          title="Thông báo cho khách"
          lead={<p className="adm-lead">{o.product_name}</p>}
          label="Nội dung"
          rows={5}
          required
          confirm="Gửi và kết đơn"
          run={(msg) => api.admin.notifyOrder(o.id, msg)}
          onDone={() => after(`Đã kết đơn ${o.trans_id}.`)}
        />,
      )
    } else if (kind === 'refund') {
      setModal(
        <NoteModal
          {...common}
          title="Hủy đơn và hoàn tiền"
          lead={
            <p className="adm-lead">
              Hoàn <strong>{num(o.total)} {refundUnit(o.pay)}</strong> cho {o.user.name}
            </p>
          }
          label="Lí do"
          required
          confirm="Hoàn tiền"
          tone="danger"
          run={(note) => api.admin.refundOrder(o.id, note)}
          onDone={() => after(`Đã hoàn tiền đơn ${o.trans_id}.`)}
        />,
      )
    } else {
      setModal(
        <NoteModal
          {...common}
          title="Hủy đơn không hoàn tiền"
          lead={<p className="adm-lead">{o.product_name} · {money(o.total, o.pay)}</p>}
          label="Lí do"
          required
          confirm="Hủy đơn"
          tone="danger"
          run={(note) => api.admin.cancelOrder(o.id, note)}
          onDone={() => after(`Đã hủy đơn ${o.trans_id}.`)}
        />,
      )
    }
  }

  return (
    <>
      <AdminHead title="Đơn chờ giao" />
      <Feedback flash={flash} err={err} />
      {data?.items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table adm-table">
            <thead>
              <tr>
                <th>Mã đơn</th>
                <th>Khách</th>
                <th>Sản phẩm</th>
                <th>Khách ghi chú</th>
                <th>SL</th>
                <th>Tổng</th>
                <th>Ngày</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id}>
                  <td>
                    <Link className="adm-mono" to={`/admin/tra-cuu?code=${o.trans_id}`}>
                      {o.trans_id.slice(0, 10)}
                    </Link>
                  </td>
                  <td>
                    <UserCell u={o.user} />
                  </td>
                  <td>{o.product_name || <span className="muted">Sản phẩm bị xóa</span>}</td>
                  <td className="adm-text">{o.user_note || '—'}</td>
                  <td>
                    <strong>{o.quantity}</strong>
                  </td>
                  <td className="acc-nowrap">
                    {money(o.total, o.pay)}
                    <small>
                      {o.pay ? PAY_LABEL[o.pay] : '—'}
                      {o.auto ? ' · tự động' : ''}
                    </small>
                  </td>
                  <td className="acc-nowrap">{formatDateTime(o.created_at)}</td>
                  <td>
                    <div className="adm-actions adm-actions-row">
                      <button type="button" className="btn btn-sm btn-ok" onClick={() => open(o, 'deliver')}>
                        <i className="fa-solid fa-truck-fast" aria-hidden /> Giao hàng
                      </button>
                      <details className="adm-more">
                        <summary className="btn btn-sm btn-line">
                          <i className="fa-solid fa-ellipsis" aria-hidden /> Khác
                        </summary>
                        <div
                          className="adm-more-menu"
                          onClick={(e) => e.currentTarget.closest('details')?.removeAttribute('open')}
                        >
                          <button type="button" onClick={() => open(o, 'notify')}>
                            <i className="fa-solid fa-bell" aria-hidden /> Thông báo
                          </button>
                          <button type="button" onClick={() => open(o, 'refund')}>
                            <i className="fa-solid fa-rotate-left" aria-hidden /> Hoàn tiền
                          </button>
                          <button type="button" onClick={() => open(o, 'cancel')}>
                            <i className="fa-solid fa-ban" aria-hidden /> Hủy không hoàn
                          </button>
                        </div>
                      </details>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ListFoot data={data} empty="Không có đơn chờ giao." onPage={(p) => set({ page: p })} />
      {modal}
    </>
  )
}

/* ---------- Tra cứu đơn theo mã ---------- */

function ItemList({ items }: { items: { id: number; text: string }[] }) {
  if (!items.length) return <p className="muted">Không có.</p>
  return (
    <ol className="adm-items">
      {items.map((i) => (
        <li key={i.id}>{i.text}</li>
      ))}
    </ol>
  )
}

function ReplaceForm({ tx, onDone }: { tx: AdminTransaction; onDone: (msg: string) => void }) {
  const [items, setItems] = useState('')
  const { busy, err, submit } = useSubmit(
    () => api.admin.replaceItems(tx.id, items),
    () => {
      setItems('')
      onDone('Đã gửi hàng đổi cho khách.')
    },
  )
  return (
    <form className="form-stack adm-replace" onSubmit={submit}>
      <label>
        Hàng đổi cho khách
        <textarea rows={4} value={items} onChange={(e) => setItems(e.target.value)} placeholder="Mỗi dòng là 1 sản phẩm" />
      </label>
      {err ? <p className="note">{err}</p> : null}
      <div>
        <button type="submit" className="btn btn-primary" disabled={busy || !items.trim()}>
          <i className="fa-solid fa-right-left" aria-hidden /> {busy ? 'Đang gửi…' : 'Gửi hàng đổi'}
        </button>
      </div>
    </form>
  )
}

export function AdminLookupPage() {
  const [params, set] = useQueryState()
  const code = params.get('code') || ''
  const [tx, setTx] = useState<AdminTransaction | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [tick, setTick] = useState(0)
  const { flash, after, modal, setModal, close } = useAfter(() => setTick((t) => t + 1))

  useEffect(() => {
    if (!code) {
      setTx(null)
      setErr('')
      return
    }
    let alive = true
    setLoading(true)
    setErr('')
    api.admin
      .lookup(code)
      .then((r) => alive && setTx(r.transaction))
      .catch((e: Error) => {
        if (!alive) return
        setTx(null)
        setErr(e.message)
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [code, tick])

  function refund(t: AdminTransaction) {
    setModal(
      <NoteModal
        title="Hoàn tiền đơn hàng"
        lead={
          <p className="adm-lead">
            Hoàn <strong>{num(t.total)} {refundUnit(t.pay)}</strong> cho {t.user.name}
          </p>
        }
        label="Lí do"
        confirm="Hoàn tiền"
        tone="danger"
        onClose={close}
        run={(note) => api.admin.refundTransaction(t.id, note)}
        onDone={() => after(`Đã hoàn tiền đơn ${t.trans_id}.`)}
      />,
    )
  }

  const rows: [string, ReactNode][] = tx
    ? [
        ['Mã đơn', <span className="adm-mono">{tx.trans_id}</span>],
        ['Khách', <UserCell u={tx.user} />],
        ['Sản phẩm', tx.product_name || '—'],
        ['Đơn giá', money(tx.unit_price, tx.pay)],
        ['Số lượng', tx.quantity],
        ['Giảm giá', money(tx.discount, tx.pay)],
        ['Tổng tiền', <strong>{money(tx.total, tx.pay)}</strong>],
        ['Thanh toán', tx.pay ? PAY_LABEL[tx.pay] : '—'],
        ['Ngày tạo', formatDateTime(tx.created_at)],
        ['Trạng thái', <StatusBadge status={tx.status} />],
        ...(tx.verify_code ? ([['Nội dung CK', <span className="adm-mono">{tx.verify_code}</span>]] as [string, ReactNode][]) : []),
        ...(tx.queue
          ? ([
              ['Nhân vật', tx.queue.character_name || '—'],
              ['Đặt hộ', <><StatusBadge status={tx.queue.status} map={QUEUE_STATUS} /> #{tx.queue.id}</>],
            ] as [string, ReactNode][])
          : []),
        ...(tx.user_note ? ([['Khách ghi chú', tx.user_note]] as [string, ReactNode][]) : []),
        ...(tx.admin_note ? ([['Admin ghi chú', <span className="acc-admin-note adm-pre">{tx.admin_note}</span>]] as [string, ReactNode][]) : []),
        ...(tx.deliver_note ? ([['Thông báo giao', <span className="adm-pre">{tx.deliver_note}</span>]] as [string, ReactNode][]) : []),
      ]
    : []

  return (
    <>
      <AdminHead title="Tra cứu đơn">
        <SearchBox value={code} onSearch={(v) => set({ code: v || null })} placeholder="Mã đơn hoặc nội dung chuyển khoản" />
      </AdminHead>
      <Feedback flash={flash} err={err} />
      {loading && !tx ? <p className="muted">Đang tải…</p> : null}
      {!code ? <p className="muted">Nhập mã đơn để tra cứu.</p> : null}
      {tx ? (
        <div className="adm-detail">
          <dl className="adm-dl">
            {rows.map(([k, v]) => (
              <div key={k} className="adm-dl-row">
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          {tx.can_refund ? (
            <div className="adm-actions">
              <button type="button" className="btn btn-sm btn-danger" onClick={() => refund(tx)}>
                <i className="fa-solid fa-rotate-left" aria-hidden /> Hoàn tiền
              </button>
            </div>
          ) : null}
          {tx.type !== 'buyitforme' && tx.type !== 'cape' ? (
            <>
              <h3>Sản phẩm đã giao</h3>
              <ItemList items={tx.items} />
              <h3>Sản phẩm đã đổi</h3>
              <ItemList items={tx.replace_items} />
            </>
          ) : null}
          {tx.can_replace ? <ReplaceForm tx={tx} onDone={after} /> : null}
        </div>
      ) : null}
      {modal}
    </>
  )
}

/* ---------- Duyệt chuyển khoản ---------- */

const DEPOSIT_TABS: [string, string][] = [
  ['chua_thanh_toan', 'Chờ duyệt'],
  ['da_thanh_toan', 'Đã duyệt'],
  ['huy_bo', 'Đã hủy'],
  ['all', 'Tất cả'],
]

function ApproveDepositModal({ d, onClose, onDone }: { d: AdminDeposit; onClose: () => void; onDone: () => void }) {
  const [received, setReceived] = useState(String(d.amount))
  const [note, setNote] = useState('')
  const value = Number(received)
  const { busy, err, submit } = useSubmit(() => api.admin.approveDeposit(d.id, value, note.trim()), onDone)
  return (
    <Modal title="Duyệt chuyển khoản" onClose={onClose}>
      <p className="adm-lead">
        <span className="adm-mono">{d.code}</span> · {d.user.name} · {formatVnd(d.amount)}
      </p>
      <form className="form-stack" onSubmit={submit}>
        <label>
          Số tiền thực nhận (cộng ví gem)
          <input type="number" min={1} step={1} value={received} onChange={(e) => setReceived(e.target.value)} autoFocus required />
        </label>
        <label>
          Ghi chú
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
        </label>
        {err ? <p className="note">{err}</p> : null}
        <ModalActions
          busy={busy}
          confirm={value > 0 ? `Cộng ${num(value)} gem` : 'Duyệt'}
          tone="ok"
          disabled={!(value > 0)}
          onClose={onClose}
        />
      </form>
    </Modal>
  )
}

export function AdminDepositsPage() {
  const [params, set] = useQueryState()
  const status = params.get('status') || 'chua_thanh_toan'
  const q = params.get('q') || ''
  const page = Number(params.get('page')) || 1
  const { data, err, reload } = useList(() => api.admin.deposits({ status, q, page }), params.toString())
  const { flash, after, modal, setModal, close } = useAfter(reload)

  return (
    <>
      <AdminHead title="Duyệt chuyển khoản">
        <SearchBox value={q} onSearch={(v) => set({ q: v || null, page: null })} placeholder="Nội dung CK, tên, email" />
      </AdminHead>
      <StatusTabs
        value={status}
        options={DEPOSIT_TABS}
        onChange={(v) => set({ status: v === 'chua_thanh_toan' ? null : v, page: null })}
      />
      <Feedback flash={flash} err={err} />
      {data?.items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table adm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Khách</th>
                <th>Nội dung CK</th>
                <th>Số tiền</th>
                <th>Thực nhận</th>
                <th>Ngày</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id}>
                  <td>{d.id}</td>
                  <td>
                    <UserCell u={d.user} />
                  </td>
                  <td className="adm-mono">{d.code}</td>
                  <td className="acc-nowrap">{formatVnd(d.amount)}</td>
                  <td className="acc-nowrap">{d.received == null ? '—' : `${num(d.received)} gem`}</td>
                  <td className="acc-nowrap">{formatDateTime(d.created_at)}</td>
                  <td className="adm-status">
                    {d.status === 'chua_thanh_toan' ? (
                      <div className="adm-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-ok"
                          onClick={() =>
                            setModal(
                              <ApproveDepositModal
                                d={d}
                                onClose={close}
                                onDone={() => after(`Đã duyệt phiếu ${d.code}.`)}
                              />,
                            )
                          }
                        >
                          <i className="fa-solid fa-check" aria-hidden /> Duyệt
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-line"
                          onClick={() =>
                            setModal(
                              <NoteModal
                                title="Hủy phiếu nạp"
                                lead={<p className="adm-lead"><span className="adm-mono">{d.code}</span> · {formatVnd(d.amount)}</p>}
                                label="Ghi chú"
                                confirm="Hủy phiếu"
                                tone="danger"
                                onClose={close}
                                run={(note) => api.admin.denyDeposit(d.id, note)}
                                onDone={() => after(`Đã hủy phiếu ${d.code}.`)}
                              />,
                            )
                          }
                        >
                          <i className="fa-solid fa-xmark" aria-hidden /> Hủy
                        </button>
                      </div>
                    ) : (
                      <>
                        <StatusBadge status={d.status} map={DEPOSIT_STATUS} />
                        {d.admin_note ? <small className="acc-admin-note">{d.admin_note}</small> : null}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ListFoot data={data} empty="Không có phiếu nạp." onPage={(p) => set({ page: p })} />
      {modal}
    </>
  )
}

/* ---------- Duyệt thẻ cào ---------- */

const CARD_TABS: [string, string][] = [
  ['choduyet', 'Chờ duyệt'],
  ['thatbai', 'Thất bại'],
  ['thanhcong', 'Thành công'],
  ['all', 'Tất cả'],
]

const CARD_AMOUNTS = [10000, 20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000]

function CreditCardModal({ c, onClose, onDone }: { c: AdminCard; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState(CARD_AMOUNTS.includes(c.amount) ? c.amount : CARD_AMOUNTS[0])
  const { busy, err, submit } = useSubmit(() => api.admin.creditCard(c.id, amount), onDone)
  return (
    <Modal title="Cộng tiền thẻ cào" onClose={onClose}>
      <p className="adm-lead">
        {c.telco} · <span className="adm-mono">{c.serial}</span> · {c.user.name}
      </p>
      <form className="form-stack" onSubmit={submit}>
        <label>
          Mệnh giá đúng (cộng ví xu)
          <select value={amount} onChange={(e) => setAmount(Number(e.target.value))}>
            {CARD_AMOUNTS.map((a) => (
              <option key={a} value={a}>
                {num(a)}
              </option>
            ))}
          </select>
        </label>
        {err ? <p className="note">{err}</p> : null}
        <ModalActions busy={busy} confirm={`Cộng ${num(amount)} xu`} tone="ok" onClose={onClose} />
      </form>
    </Modal>
  )
}

function CardFilter({ params, set }: { params: URLSearchParams; set: (p: Record<string, string | null>) => void }) {
  const [f, setF] = useState({
    serial: params.get('serial') || '',
    pin: params.get('pin') || '',
    from: params.get('from') || '',
    to: params.get('to') || '',
  })
  return (
    <form
      className="adm-filter"
      onSubmit={(e) => {
        e.preventDefault()
        set({ serial: f.serial.trim() || null, pin: f.pin.trim() || null, from: f.from || null, to: f.to || null, page: null })
      }}
    >
      <label>
        Serial
        <input value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} />
      </label>
      <label>
        Mã thẻ
        <input value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value })} />
      </label>
      <label>
        Từ ngày
        <input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
      </label>
      <label>
        Đến ngày
        <input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
      </label>
      <button type="submit" className="btn btn-primary btn-sm">
        <i className="fa-solid fa-magnifying-glass" aria-hidden /> Lọc
      </button>
    </form>
  )
}

export function AdminCardsPage() {
  const [params, set] = useQueryState()
  const status = params.get('status') || 'choduyet'
  const page = Number(params.get('page')) || 1
  const filter = {
    status,
    page,
    serial: params.get('serial') || '',
    pin: params.get('pin') || '',
    from: params.get('from') || '',
    to: params.get('to') || '',
  }
  const { data, err, reload } = useList(() => api.admin.cards(filter), params.toString())
  const { flash, after, modal, setModal, close } = useAfter(reload)

  return (
    <>
      <AdminHead title="Duyệt thẻ cào" />
      <CardFilter params={params} set={set} />
      <StatusTabs value={status} options={CARD_TABS} onChange={(v) => set({ status: v === 'choduyet' ? null : v, page: null })} />
      <Feedback flash={flash} err={err} />
      {data?.items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table adm-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nhà mạng</th>
                <th>Serial</th>
                <th>Mã thẻ</th>
                <th>Mệnh giá</th>
                <th>Khách</th>
                <th>Ngày</th>
                <th>Trạng thái</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>{c.telco}</td>
                  <td className="adm-mono acc-nowrap">{c.serial}</td>
                  <td className="adm-mono acc-nowrap">{c.pin}</td>
                  <td className="acc-nowrap">{num(c.amount)}</td>
                  <td>
                    <UserCell u={c.user} />
                  </td>
                  <td className="acc-nowrap">{formatDateTime(c.created_at)}</td>
                  <td className="adm-status">
                    <StatusBadge status={c.status} map={CARD_STATUS} />
                    {c.note ? <small>{c.note}</small> : null}
                  </td>
                  <td>
                    {c.status !== 'thanhcong' ? (
                      <div className="adm-actions adm-actions-row">
                        <button
                          type="button"
                          className="btn btn-sm btn-ok"
                          onClick={() =>
                            setModal(
                              <CreditCardModal c={c} onClose={close} onDone={() => after(`Đã cộng tiền thẻ #${c.id}.`)} />,
                            )
                          }
                        >
                          <i className="fa-solid fa-coins" aria-hidden /> Cộng tiền
                        </button>
                        <details className="adm-more">
                          <summary className="btn btn-sm btn-line">
                            <i className="fa-solid fa-ellipsis" aria-hidden /> Khác
                          </summary>
                          <div
                            className="adm-more-menu"
                            onClick={(e) => e.currentTarget.closest('details')?.removeAttribute('open')}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setModal(
                                  <NoteModal
                                    title="Chuyển trạng thái thành công"
                                    lead={<p className="adm-lead">Thẻ #{c.id} · {num(c.amount)} · không cộng tiền</p>}
                                    confirm="Xác nhận"
                                    onClose={close}
                                    run={() => api.admin.markCard(c.id)}
                                    onDone={() => after(`Thẻ #${c.id} đã chuyển thành công.`)}
                                  />,
                                )
                              }
                            >
                              <i className="fa-solid fa-arrows-rotate" aria-hidden /> Chuyển thành công
                            </button>
                          </div>
                        </details>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ListFoot data={data} empty="Không có thẻ." onPage={(p) => set({ page: p })} />
      {modal}
    </>
  )
}

/* ---------- Thành viên: cộng / trừ số dư ---------- */

const walletOf = (v: string | null) => (/gem/i.test(v || '') ? 'gem' : 'xu')

function BalanceModal({ id, onClose, onDone }: { id: number; onClose: () => void; onDone: (msg: string) => void }) {
  const [m, setM] = useState<AdminMemberDetail | null>(null)
  const [loadErr, setLoadErr] = useState('')
  const [wallet, setWallet] = useState<'xu' | 'gem'>('xu')
  const [op, setOp] = useState<'cong' | 'tru'>('cong')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const value = Number(amount)

  useEffect(() => {
    api.admin
      .member(id)
      .then((r) => setM(r.member))
      .catch((e: Error) => setLoadErr(e.message))
  }, [id])

  const { busy, err, submit } = useSubmit(
    () => api.admin.adjustBalance(id, { wallet, op, amount: value, note: note.trim() }),
    () => onDone(`Đã ${op === 'cong' ? 'cộng' : 'trừ'} ${num(value)} ${wallet} cho ${m?.name}.`),
  )

  return (
    <Modal title="Thay đổi số dư" onClose={onClose} wide>
      {loadErr ? <p className="note">{loadErr}</p> : null}
      {!m && !loadErr ? <p className="muted">Đang tải…</p> : null}
      {m ? (
        <>
          <p className="adm-lead">
            <strong>{m.name}</strong> · {m.email}
          </p>
          <div className="adm-balances">
            <span>
              <i className="fa-solid fa-coins" aria-hidden /> {num(m.balance)} xu
            </span>
            <span>
              <i className="fa-regular fa-gem" aria-hidden /> {num(m.balance2)} gem
            </span>
          </div>
          <form className="form-stack" onSubmit={submit}>
            <div className="adm-seg-row">
              <StatusTabs value={wallet} options={[['xu', 'Ví xu'], ['gem', 'Ví gem']]} onChange={(v) => setWallet(v as 'xu' | 'gem')} />
              <StatusTabs value={op} options={[['cong', 'Cộng tiền'], ['tru', 'Trừ tiền']]} onChange={(v) => setOp(v as 'cong' | 'tru')} />
            </div>
            <label>
              Số tiền
              <input type="number" min={1} step={1} value={amount} onChange={(e) => setAmount(e.target.value)} required autoFocus />
            </label>
            <label>
              Lí do
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} required />
            </label>
            {err ? <p className="note">{err}</p> : null}
            <ModalActions
              busy={busy}
              confirm={value > 0 ? `${op === 'cong' ? 'Cộng' : 'Trừ'} ${num(value)} ${wallet}` : 'Xác nhận'}
              tone={op === 'cong' ? 'ok' : 'danger'}
              disabled={!(value > 0) || !note.trim()}
              onClose={onClose}
            />
          </form>
          {m.changes.length ? (
            <>
              <h3 className="adm-sub">Biến động gần đây</h3>
              <ul className="adm-changes">
                {m.changes.map((c) => (
                  <li key={c.id}>
                    <span className={c.kind === 'cong' ? 'adm-plus' : 'adm-minus'}>
                      {c.kind === 'cong' ? '+' : '−'}
                      {num(c.amount)} {walletOf(c.wallet)}
                    </span>
                    <span className="adm-text">{c.note || '—'}</span>
                    <small>{formatDateTime(c.created_at)}</small>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      ) : null}
    </Modal>
  )
}

export function AdminMembersPage() {
  const [params, set] = useQueryState()
  const q = params.get('q') || ''
  const page = Number(params.get('page')) || 1
  const { data, err, reload } = useList(() => api.admin.members({ q, page }), params.toString())
  const { flash, after, modal, setModal, close } = useAfter(reload)

  function openBalance(m: AdminMember) {
    setModal(<BalanceModal id={m.id} onClose={close} onDone={after} />)
  }

  return (
    <>
      <AdminHead title="Số dư thành viên">
        <SearchBox value={q} onSearch={(v) => set({ q: v || null, page: null })} placeholder="ID, tên, email" />
      </AdminHead>
      <Feedback flash={flash} err={err} />
      {data?.items.length ? (
        <div className="acc-table-wrap">
          <table className="acc-table adm-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Thành viên</th>
                <th>Ví xu</th>
                <th>Ví gem</th>
                <th>Ngày tạo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.items.map((m) => (
                <tr key={m.id}>
                  <td>{m.id}</td>
                  <td>
                    <strong>{m.name}</strong>
                    <small>{m.email}</small>
                  </td>
                  <td className="acc-nowrap">{num(m.balance)}</td>
                  <td className="acc-nowrap">{num(m.balance2)}</td>
                  <td className="acc-nowrap">{formatDateTime(m.created_at)}</td>
                  <td>
                    <div className="adm-actions">
                      <button type="button" className="btn btn-sm btn-primary" onClick={() => openBalance(m)}>
                        <i className="fa-solid fa-scale-balanced" aria-hidden /> Số dư
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <ListFoot data={data} empty="Không tìm thấy thành viên." onPage={(p) => set({ page: p })} />
      {modal}
    </>
  )
}
